<template>
  <div class="form-wrapper">
    <div class="intro-section__form">
      <form @submit.prevent="signup">
        <div class="form-group" :class="{'form-group--error': hasValidationError('email')}">
          <input type="email" v-model="email" required
                 class="input input-lg form-control"
                 :placeholder="$t('forms.signup_form.email')"
          />
          <span class="form-group--error__message" v-if="hasValidationError('email')">
            {{ validationErrorsEmail }}
          </span>
        </div>

        <div class="form-group" :class="{'form-group--error': hasValidationError('password')}">
          <input-password
              v-model="password"
              :required="true"
              :autocomplete="'new-password'"
              :placeholder="$t('forms.signup_form.password')"
          />
          <span class="form-group--error__message" v-if="hasValidationError('password')">
            {{ validationErrors.password }}
          </span>
        </div>

        <transition name="slide">
          <div class="form-group" v-if="password">
            <input-password
                v-model="passwordConfirm"
                :required="true"
                :autocomplete="'new-password'"
                :extra-classes="'input-confirm'"
                :placeholder="$t('forms.signup_form.password_confirm')"
                :paste="() => false"
            />
          </div>
        </transition>

        <div class="form-check-container">
          <div class="form-check" v-if="getAppConfigReady">
            <div
                v-if="$store.state.appConfig.privacyCheckboxes && getPrivacyCheckboxes.length">
              <template v-for="checkbox in getPrivacyCheckboxes">
                <b-form-checkbox v-model="userCheckboxChoices[checkbox.field_name]" :required="checkbox.is_required"
                                 :name="checkbox.field_name"
                                 :value="true"
                                 :key="checkbox.field_name"
                                 :unchecked-value="false">
                  <span v-html="checkbox.label"></span>
                </b-form-checkbox>
              </template>
            </div>
            <div v-else>
              <b-form-checkbox v-model="consent_privacy_and_terms" required
                               name="consent_privacy_and_terms"
                               value="true"
                               unchecked-value="false">
                <template>
                  <span v-html="privacyStatement"></span>
                </template>
              </b-form-checkbox>
            </div>

            <span class="form-group--error__message" v-if="hasValidationError('consent_privacy_and_terms')">
              {{ validationErrors.consent_privacy_and_terms }}
            </span>
          </div>
        </div>

        <div class="form-group">
          <button type="submit" class="btn btn-primary m-0 w-100" :disabled="loading">
            <b-spinner small label="Loading..." v-if="loading"></b-spinner>
            <span v-else>{{ $t('forms.signup_form.create_account_btn') }}</span>
          </button>
        </div>
      </form>
    </div>

    <SocialLogin v-if="showSocialLogin"/>

    <BottomPoweredBy/>
  </div>
</template>

<script>
import UserService from "@/services/User.js"
import {mapGetters} from "vuex";
import {isNavigationFailure} from "vue-router";
import InputPassword from "@/components/InputPassword";
import SocialLogin from "@/components/SocialLogin";
import BottomPoweredBy from "@/components/BottomPoweredBy.vue";
import {policyUrl} from "@/utils";

export default {
  components: {BottomPoweredBy, SocialLogin, InputPassword},
  data() {
    return {
      email: "",
      password: "",
      passwordConfirm: "",
      consent_privacy_and_terms: false,
      loading: false,
      validationErrors: {},
      userCheckboxChoices: {}
    }
  },

  props: {
    backHomeBtnTxt: {
      type: String,
      required: false
    }
  },
  watch: {
    getAppConfigReady: {
      handler() {
        this.setUserCheckboxChoices()
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters(['thirdLevelDomain']),
    ...mapGetters('appConfig', [
      'getPrivacyCheckboxes', 'getAppConfigReady', 'getEventOwnerId', 'disableSocialLogin'
    ]),
    useStrongPassword() {
      return this.thirdLevelDomain !== undefined && this.thirdLevelDomain.includes('watchesandwonders')
    },
    showSocialLogin() {
      if (!this.getAppConfigReady) return false
      if (this.disableSocialLogin) return false
      if (['photos'].includes(this.thirdLevelDomain)) return false
      return true
    },

    validationErrorsEmail() {
      return this.validationErrors.email[0]
    },
    privacyStatement() {
      return this.$t('forms.signup_form.privacy_statement', {
        terms_url: this.policyUrl().terms,
        privacy_url: this.policyUrl().privacy
      })
    },
  },

  methods: {
    policyUrl,
    setUserCheckboxChoices() {
      let defaultModel = {}
      const checkboxes = this.getPrivacyCheckboxes
      checkboxes.forEach(function (checkbox) {
        defaultModel[checkbox.field_name] = false
      });
      this.$set(this, "userCheckboxChoices", defaultModel)
    },

    validateFormData() {
      this.validationErrors = {};

      if (this.password !== this.passwordConfirm) {
        this.$set(this.validationErrors, 'password', this.$t('forms.signup_form.password_error_match'));
        return false;
      }

      if (this.useStrongPassword) {
        const passwordRegex = /^(?=.*\d)(?=.*[A-Z])(?=.*\W).{8,}$/;
        if (!passwordRegex.test(this.password)) {
          this.$set(this.validationErrors, 'password', this.$t('forms.signup_form.password_error_invalid'));
          return false;
        }
      }

      return true;
    },

    signup() {
      // Ignore a second submit while account creation + auto login are in progress
      if (this.loading) return false
      if (!this.validateFormData()) {
        return false
      }

      this.validationErrors = {...this.validationErrors}
      this.loading = true
      let payload = {
        email: this.email,
        password: this.password,
        language: this.$i18n.locale
      }

      payload.event_owner_id = this.getEventOwnerId;

      if (this.getEventOwnerId && this.getPrivacyCheckboxes.length) {
        payload.consent_third_party_fields = this.userCheckboxChoices
      } else {
        payload.consent_privacy_and_terms = this.consent_privacy_and_terms
      }

      UserService.createAccount(payload)
          .then(() => {
            // login automatico (loading stays on until it completes)
            const data = {
              email: this.email,
              password: this.password
            }
            this.$store
                .dispatch("auth/login", data)
                .then(() => {
                  this.loading = false
                  this.$router.push({name: "checkins-list"}).catch(err => {
                    if (!isNavigationFailure(err)) throw err
                  })
                  if (this.$store.getters['showMarketingCheck']) {
                    this.$bvModal.show('modal-marketing-consent')
                  }
                })
                .catch(err => {
                  this.loading = false
                  console.log("auth/login error", err.response.data)
                })
          })
          .catch(error => {
            this.loading = false
            console.log('signup error', error.response.data)
            this.validationErrors = Object.assign({}, this.validationErrors, error.response.data)
          })
    },

    hasValidationError(fieldName) {
      return this.validationErrors.hasOwnProperty(fieldName)
    }
  }
}
</script>
